import { equals } from 'ramda';
import { Point, Range } from 'slate';
import type { BaseRange, NodeEntry, Path } from 'slate';

import type { RenderLineOptions, SuggestedParagraphMark } from './line';
import { renderLine } from './line';
import { renderText } from './text';
import { isParagraphGlyphEdit, Queries } from '../../slate-api';
import type {
  DeleteParagraphEdit,
  ParagraphBorders,
  RenderModelParagraph,
  TableBorder,
} from '../../slate-api';
import { activeSelectionColor, inactiveSelectionColor, nonPrintingCharacterColor } from '../colors';
import type { RenderContext } from '../engine/render-context';
import type { BaseRenderNodeOptions } from '../engine/rendering-data';
import type { SpellcheckRangeRef, SpellcheckRetiredFrom } from '../spellcheck';
import { isSpellcheckRedlineHidden } from '../spellcheck';
import { CANVAS_Z_INDICES } from '../z-indices';

export interface RenderModelSearchHit extends BaseRange {
  isActive: boolean;
}

export function renderParagraph(
  renderContext: RenderContext,
  rMParagraphEntry: NodeEntry<RenderModelParagraph>,
  options: BaseRenderNodeOptions
) {
  const { editor } = renderContext;
  const [rMParagraph, rMParagraphPath] = rMParagraphEntry;
  const cMParagraphPath = renderContext.cMPath;
  const {
    borderMergesWithNext,
    borderMergesWithPrev,
    bordersToDraw,
    children,
    heightPx,
    listMarker,
    widthPx,
  } = rMParagraph;
  const { fillColor } = Queries.renderedParagraphShade(rMParagraph) ?? {};
  const search = renderContext.withContext('search');
  const matchCase = renderContext.withContext('matchCase');
  const matchAccents = renderContext.withContext('matchAccents');

  // Determine the search hits for this paragraph
  const rMSearchHitsRanges: Range[] = search
    ? renderContext.withEditor(editor => {
        return Queries.getRMSearchHits(editor, {
          matchAccents,
          matchCase,
          rMParagraphEntry,
          search,
        });
      })
    : [];

  let rMSearchHits: RenderModelSearchHit[] = [];

  if (rMSearchHitsRanges.length) {
    const { cMRange } = renderContext;

    const activeSearchHit =
      cMRange &&
      renderContext.withContextSelector('activeSearchHit', activeSearchHit =>
        activeSearchHit && Range.intersection(cMRange, activeSearchHit) ? activeSearchHit : null
      );

    // Convert without `withEditor`, so search-hit rendering isn't invalidated on every editor change.
    const activeSearchHitRMRange =
      activeSearchHit && cMParagraphPath
        ? Queries.getRMRangeFromCMRangeInParagraph({
            cMParagraphPath,
            cMRange: activeSearchHit,
            rMParagraph,
            rMParagraphPath,
          })
        : null;

    rMSearchHits = rMSearchHitsRanges.map(range => ({
      ...range,
      isActive: activeSearchHitRMRange ? Range.equals(range, activeSearchHitRMRange) : false,
    }));
  }

  /**
   * Subscribe with a selector reading only this paragraph's entry, not the whole map: a plain
   * `withContext` would re-render every paragraph whenever any range changes, since the map is
   * rebuilt by identity on each re-lint. Unwrap `.current` at convert time so an edit in this
   * paragraph moves the underline without a republish.
   */
  const paragraphKey = cMParagraphPath?.join(',') ?? null;
  const paragraphSpellcheckRanges = paragraphKey
    ? renderContext.withContextSelector(
        'spellcheckRangesByParagraph',
        map => map?.get(paragraphKey) ?? null
      )
    : null;
  const retiredFrom = paragraphKey
    ? renderContext.withContextSelector('spellcheckRetiredFromByParagraph', map =>
        map?.get(paragraphKey)
      )
    : undefined;
  const rMSpellcheckHits =
    cMParagraphPath && paragraphSpellcheckRanges?.length
      ? convertParagraphSpellcheckRanges({
          cMParagraphPath,
          cMRange: renderContext.cMRange,
          paragraphSpellcheckRanges,
          rMParagraph,
          rMParagraphPath,
          retiredFrom,
        })
      : [];

  /**
   * Like Word, shading and borders cover the full paragraph box, which extends
   * left of the content box over a hanging first line / list number.
   *
   * Spacing before/after stays outside the unmerged border box. Top/bottom
   * centerlines sit half a thickness inside that box. In a merged run,
   * verticals always continue through spacing (start at 0 / end at height)
   * so adjacent segments join into one continuous side — including when a
   * `between` horizontal is drawn on the shared edge.
   */
  const leftExtentPx = Queries.renderedParagraphBorderLeftExtent(rMParagraph, 'px');
  const spacingBeforePx = Queries.renderedSpacingBeforePx(rMParagraph);
  const spacingAfterPx = children.at(-1)?.isLastLine
    ? Queries.renderedParagraphSpacingAfter(rMParagraph, 'px')
    : 0;
  const topOuterPx = spacingBeforePx;
  const bottomOuterPx = heightPx - spacingAfterPx;
  const topWidthPx = bordersToDraw?.top
    ? Queries.renderedParagraphBorderWidth(bordersToDraw.top, 'px')
    : 0;
  const bottomWidthPx = bordersToDraw?.bottom
    ? Queries.renderedParagraphBorderWidth(bordersToDraw.bottom, 'px')
    : 0;
  const topCenterPx = topOuterPx + topWidthPx / 2;
  const bottomCenterPx = bottomOuterPx - bottomWidthPx / 2;
  const verticalStartPx = borderMergesWithPrev ? 0 : topOuterPx;
  const verticalEndPx = borderMergesWithNext ? heightPx : bottomOuterPx;
  const leftSpacePx = convertBorderSpaceToPx(bordersToDraw?.left);
  const rightSpacePx = convertBorderSpaceToPx(bordersToDraw?.right);
  const leftWidthPx = bordersToDraw?.left
    ? Queries.renderedParagraphBorderWidth(bordersToDraw.left, 'px')
    : 0;
  const rightWidthPx = bordersToDraw?.right
    ? Queries.renderedParagraphBorderWidth(bordersToDraw.right, 'px')
    : 0;
  const leftCenterPx = leftWidthPx ? leftExtentPx - leftSpacePx : leftExtentPx;
  const rightCenterPx = rightWidthPx ? widthPx + rightSpacePx : widthPx;
  /**
   * Shade fills the border-box interior up to the inner edge of each stroke
   * (covering `w:space` padding). Merged edges keep the through-spacing span so
   * adjacent shaded paragraphs join. With no side border, keep the content edge.
   */
  const shadeLeftPx = leftWidthPx ? leftCenterPx + leftWidthPx / 2 : leftExtentPx;
  const shadeRightPx = rightWidthPx ? rightCenterPx - rightWidthPx / 2 : widthPx;
  const shadeTopPx =
    topWidthPx && !borderMergesWithPrev ? topCenterPx + topWidthPx / 2 : verticalStartPx;
  const shadeBottomPx =
    bottomWidthPx && !borderMergesWithNext ? bottomCenterPx - bottomWidthPx / 2 : verticalEndPx;

  // Paragraph shade
  if (fillColor)
    renderContext.drawRect(
      shadeLeftPx,
      shadeTopPx,
      shadeRightPx - shadeLeftPx,
      shadeBottomPx - shadeTopPx,
      {
        fillColor,
        zIndex: CANVAS_Z_INDICES.PARAGRAPH_BACKGROUND,
      }
    );

  if (bordersToDraw) {
    renderParagraphBorders(renderContext, bordersToDraw, {
      bottomCenterPx,
      leftCenterPx,
      leftWidthPx,
      rightCenterPx,
      rightWidthPx,
      topCenterPx,
      verticalEndPx,
      verticalStartPx,
    });
  }

  // List marker
  for (const boundingBox of Queries.getRMSpecialBoundingBoxes(editor, rMParagraphEntry)) {
    if (boundingBox.type !== 'list-marker') continue;
    const { leftPx, topPx } = boundingBox;

    const firstRMLine = children.at(0);
    if (!listMarker || !firstRMLine?.isFirstLine) continue;
    const { baselinePx, maxDescentPx } = firstRMLine;

    const isSelected = renderContext.withEditor(
      editor =>
        !!rMParagraph.listProps && equals(editor.selection?.listMarker, rMParagraph.listProps)
    );

    const selectionColor = isSelected
      ? renderContext.withContext('isFocused')
        ? activeSelectionColor.toString()
        : inactiveSelectionColor.toString()
      : undefined;

    renderContext.renderSpecial(leftPx, topPx, () => {
      renderText(renderContext, [listMarker], {
        ...options,
        baselinePx,
        customHighlight: selectionColor ? { color: selectionColor } : undefined,
        lineHeightPx: firstRMLine.heightPx,
        lineMaxDescentPx: maxDescentPx,
        lineMaxFontSizePx: Queries.renderedFontSize(listMarker),
        rMSearchHits: [],
        spaceWidthPx: 'default',
      });
    });
  }

  const showNonPrintingCharacters = renderContext.withContext('showNonPrintingCharacters');
  const glyphEdits = rMParagraph.glyphProps?.suggestedEdits ?? [];
  const deleteEdit = glyphEdits.find(
    (edit): edit is DeleteParagraphEdit => edit.type === 'deleteParagraph'
  );

  /**
   * The right-margin pencil tracks the paragraph's glyph edits independently of the ¶:
   * a suggested insertion shows the pencil but no ¶; a "show formatting marks" ¶ shows
   * no pencil.
   */
  const annotationMarkerGlyphEdits = glyphEdits.filter(isParagraphGlyphEdit);

  const suggestedParagraphMark = buildSuggestedParagraphMark({
    deleteEdit,
    showNonPrintingCharacters,
  });
  if (suggestedParagraphMark)
    suggestedParagraphMark.selectionHighlightColor = pilcrowSelectionColor(renderContext);

  renderContext.renderChildren(rMParagraphEntry, { line: renderLine }, {
    ...options,
    annotationMarkerGlyphEdits,
    rMSearchHits,
    rMSpellcheckHits,
    suggestedParagraphMark,
  } satisfies RenderLineOptions);
}

/** Clip each spellcheck range to this paragraph and convert them all in one linear pass. */
function convertParagraphSpellcheckRanges(args: {
  cMParagraphPath: Path;
  cMRange: Range | null;
  paragraphSpellcheckRanges: readonly SpellcheckRangeRef[];
  retiredFrom: SpellcheckRetiredFrom | undefined;
  rMParagraph: RenderModelParagraph;
  rMParagraphPath: Path;
}): Range[] {
  const {
    cMParagraphPath,
    cMRange,
    paragraphSpellcheckRanges,
    rMParagraph,
    rMParagraphPath,
    retiredFrom,
  } = args;
  if (!cMRange) return [];

  const cMRanges: Range[] = [];
  for (const spellcheckRangeRef of paragraphSpellcheckRanges) {
    const spellcheckRange = spellcheckRangeRef.current;
    if (spellcheckRange && !isSpellcheckRedlineHidden(spellcheckRange, retiredFrom)) {
      const intersection = Range.intersection(cMRange, spellcheckRange);
      if (intersection) cMRanges.push(intersection);
    }
  }
  const rMRanges = Array.from(
    Queries.getRMRangesForCMParagraph({
      cMParagraphPath,
      cMRanges,
      rMParagraph,
      rMParagraphPath,
    })
  );

  // `renderText` stops scanning once a hit starts after the text node it is clipping to.
  rMRanges.sort((a, b) => Point.compare(Range.start(a), Range.start(b)));
  return rMRanges;
}

/**
 * Selection highlight color for the ¶ mark, or `undefined` when it isn't selected.
 */
function pilcrowSelectionColor(renderContext: RenderContext): string | undefined {
  const { cMRange } = renderContext;
  const isSelected = renderContext.withEditor(editor => {
    const { selection } = editor;
    if (!selection || !cMRange || selection.listMarker || !Range.isExpanded(selection))
      return false;
    return Range.includes(selection, Range.end(cMRange));
  });
  if (!isSelected) return undefined;

  return renderContext.withContext('isFocused')
    ? activeSelectionColor.toString()
    : inactiveSelectionColor.toString();
}

/**
 * Builds the ¶ descriptor, or `undefined` when no ¶ should be drawn.
 */
function buildSuggestedParagraphMark(options: {
  showNonPrintingCharacters: boolean;
  deleteEdit?: DeleteParagraphEdit;
}): SuggestedParagraphMark | undefined {
  const { deleteEdit, showNonPrintingCharacters } = options;
  if (!deleteEdit && !showNonPrintingCharacters) return undefined;

  return {
    color: deleteEdit ? undefined : nonPrintingCharacterColor.toString(),
    deleteEdit,
  };
}

function renderParagraphBorders(
  renderContext: RenderContext,
  borders: ParagraphBorders,
  geometry: {
    bottomCenterPx: number;
    leftCenterPx: number;
    leftWidthPx: number;
    rightCenterPx: number;
    rightWidthPx: number;
    topCenterPx: number;
    verticalEndPx: number;
    verticalStartPx: number;
  }
) {
  const {
    bottomCenterPx,
    leftCenterPx,
    leftWidthPx,
    rightCenterPx,
    rightWidthPx,
    topCenterPx,
    verticalEndPx,
    verticalStartPx,
  } = geometry;
  /**
   * Word paragraph borders: horizontals own the corners. Verticals run between
   * horizontal outer edges, or through spacing when merging with a neighbor.
   * Horizontals span to the verticals' outer edges and paint last so they cover
   * the corner squares.
   */
  const horizontalStartPx = leftCenterPx - leftWidthPx / 2;
  const horizontalEndPx = rightCenterPx + rightWidthPx / 2;

  // Verticals first so horizontals paint over the corner squares.
  if (borders.left)
    renderParagraphBorderLine(renderContext, {
      crossAxisPosition: leftCenterPx,
      mainAxisEnd: verticalEndPx,
      mainAxisStart: verticalStartPx,
      orientation: 'vertical',
      partialBorder: borders.left,
    });
  if (borders.right)
    renderParagraphBorderLine(renderContext, {
      crossAxisPosition: rightCenterPx,
      mainAxisEnd: verticalEndPx,
      mainAxisStart: verticalStartPx,
      orientation: 'vertical',
      partialBorder: borders.right,
    });
  if (borders.top)
    renderParagraphBorderLine(renderContext, {
      crossAxisPosition: topCenterPx,
      mainAxisEnd: horizontalEndPx,
      mainAxisStart: horizontalStartPx,
      orientation: 'horizontal',
      partialBorder: borders.top,
    });
  if (borders.bottom)
    renderParagraphBorderLine(renderContext, {
      crossAxisPosition: bottomCenterPx,
      mainAxisEnd: horizontalEndPx,
      mainAxisStart: horizontalStartPx,
      orientation: 'horizontal',
      partialBorder: borders.bottom,
    });
}

function convertBorderSpaceToPx(border: Partial<TableBorder> | undefined): number {
  if (!border || !Queries.renderedParagraphBorderWidth(border, 'px')) return 0;
  return Queries.renderedParagraphBorderLayoutInsets({
    bordersToDraw: { left: border },
  }).left;
}

function renderParagraphBorderLine(
  renderContext: RenderContext,
  options: {
    crossAxisPosition: number;
    mainAxisEnd: number;
    mainAxisStart: number;
    orientation: 'horizontal' | 'vertical';
    partialBorder: Partial<TableBorder>;
  }
) {
  const { crossAxisPosition, mainAxisEnd, mainAxisStart, orientation, partialBorder } = options;
  const border = Queries.mergeTableBorderWithDefault(partialBorder);
  const borderWidth = Queries.renderedTableBorderWidth(border, 'px');
  if (!borderWidth) return;

  const { color, style } = border;
  let dashPattern: number[] | undefined;

  switch (style) {
    case 'dashed':
      dashPattern = [borderWidth * 4, borderWidth * 4];
      break;
    case 'dotted':
      dashPattern = [borderWidth, borderWidth];
      break;
  }

  if (orientation === 'horizontal') {
    renderContext.drawHorizontalLine({
      color,
      dashPattern,
      endX: mainAxisEnd,
      startX: mainAxisStart,
      thickness: borderWidth,
      y: crossAxisPosition,
    });
    return;
  }

  renderContext.drawVerticalLine({
    color,
    dashPattern,
    endY: mainAxisEnd,
    startY: mainAxisStart,
    thickness: borderWidth,
    x: crossAxisPosition,
  });
}
